// find-your-foam.js — quiz → segment → blurred product gate → email unlock

// ── STATE ────────────────────────────────────────────────────────────────────
const S = {
  cur: 0,
  answers: { problem:null, gun:null, quantity:null, budget:null }
};

// Total visible steps: intro(0) + 4 questions + result(5)
const TOTAL_STEPS = 6;

// ── ANALYTICS / WEBHOOK ──────────────────────────────────────────────────────
// TODO: replace with the real webhook URL once n8n flow is live
const WEBHOOK_URL = 'https://n8n.datontech.com/webhook/find-your-foam-submit';
const WEBHOOK_SECRET = 'e395afb4c272901cca652df547f1a2330048665722fdfda7a82f9d45a3d8d6b3';

const T = (() => {
  let sid = sessionStorage.getItem('fyf_sid');
  if (!sid) {
    sid = (crypto.randomUUID && crypto.randomUUID()) ||
          (Date.now() + '-' + Math.random().toString(36).slice(2));
    sessionStorage.setItem('fyf_sid', sid);
  }
  return {
    sessionId: sid,
    startedAt: new Date().toISOString(),
    startedAtMs: Date.now(),
    steps: [],
    currentStepId: 'q0',
    currentStepStart: Date.now(),
    sentScore: false,
    sentEmail: false,
  };
})();

function answerFor(stepId) {
  const map = { q1:'problem', q2:'gun', q3:'quantity', q4:'budget' };
  const k = map[stepId];
  return k ? S.answers[k] : null;
}

function trackNav(newId) {
  T.steps.push({
    id: T.currentStepId,
    durationMs: Date.now() - T.currentStepStart,
    answer: answerFor(T.currentStepId),
  });
  T.currentStepId = newId;
  T.currentStepStart = Date.now();
}

function buildPayload(eventType, extra = {}) {
  return {
    sessionId: T.sessionId,
    eventType,
    startedAt: T.startedAt,
    endedAt: new Date().toISOString(),
    totalDurationMs: Date.now() - T.startedAtMs,
    lastStepId: T.currentStepId,
    steps: T.steps,
    answers: S.answers,
    ...extra,
  };
}

function sendEvent(eventType, extra = {}) {
  const payload = buildPayload(eventType, extra);
  fetch(WEBHOOK_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'X-Webhook-Secret': WEBHOOK_SECRET },
    body: JSON.stringify(payload),
    keepalive: true,
  }).catch(() => {});
}

// Abandon detection
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'hidden' && T.steps.length > 0 && !T.sentEmail) {
    sendEvent('abandon');
  }
});

// ── SEGMENTS ─────────────────────────────────────────────────────────────────
// 4 segments. Products in the `spray-foam-insulation` collection are tagged
// with `segment:<key>` plus format/qty tags (`quiz-format:kit|gun-grade`,
// `quiz-qty:small|medium|large|bulk`). Filtering narrows segment → format.
const SEGMENTS = {
  'spot-fixer': {
    label: 'Quick Fix',
    sub: 'A few spots, a quick touch-up. You need a fast-applying foam that goes a long way in small amounts.',
  },
  'diy-room': {
    label: 'DIY Room',
    sub: 'You\'re sealing one room or area yourself. A mid-volume foam that\'s easy to control works best.',
  },
  'home-sealer': {
    label: 'Whole Home',
    sub: 'Multiple areas or the whole home. You need a high-yield, wide-coverage kit-type solution.',
  },
  'pro-contractor': {
    label: 'Pro Job',
    sub: 'Whole-home work or multiple buildings. You need gun-grade foam in professional volume, compatible with your foam gun.',
  },
};

// ── SCORING — answers → segment ──────────────────────────────────────────────
function calcSegment() {
  const a = S.answers;
  const q = a.quantity;
  const hasGun = a.gun === 'yes';
  const budget = a.budget; // 'economy' | 'balanced' | 'premium' — low-weight tie-breaker
  // 1–3 spots → always spot-fixer
  if (q === 'small') return 'spot-fixer';
  // Single room → DIY room
  if (q === 'room') return 'diy-room';
  // Large job + gun → pro
  if ((q === 'multi' || q === 'whole') && hasGun) {
    // tie-breaker: very budget-conscious multi-area users drop to home-sealer
    if (q === 'multi' && budget === 'economy') return 'home-sealer';
    return 'pro-contractor';
  }
  // Large job, no gun → whole home sealer
  if (q === 'multi' || q === 'whole') return 'home-sealer';
  return 'spot-fixer';
}

// Derive format / qty / intent / context tags from segment + answers.
function deriveTags(segmentKey) {
  const a = S.answers;
  const hasGun = a.gun === 'yes';
  let format;
  if (segmentKey === 'home-sealer') format = 'kit';
  else if (segmentKey === 'pro-contractor') format = 'gun-grade';
  else format = hasGun ? 'gun-grade' : 'kit';
  const qtyMap = {
    'spot-fixer':     'small',
    'diy-room':       'medium',
    'home-sealer':    'large',
    'pro-contractor': 'bulk',
  };
  return {
    segment: 'segment:' + segmentKey,
    format:  'quiz-format:' + format,
    qty:     'quiz-qty:' + qtyMap[segmentKey],
    budget:  'budget:' + (a.budget || 'balanced'),
    context: 'context:' + (a.problem || 'draft'),
  };
}

// ── PRODUCT FILTERING ────────────────────────────────────────────────────────
// Scored match:
//   segment  = required (products without it are dropped)
//   format   = +5   (kit vs gun-grade — big deal for usability)
//   qty      = +3   (quantity fit)
//   budget   = +1   (low-weight tie-breaker per spec)
//   context  = +1   (problem alignment — email copy hint, light ranking)
function pickProducts(segmentKey) {
  const all = (window.FOAM_PRODUCTS && window.FOAM_PRODUCTS.products) || [];
  if (!all.length) return [];
  const tags = deriveTags(segmentKey);

  const bySegment = all.filter(p => (p.tags || []).includes(tags.segment));
  if (!bySegment.length) return all.slice(0, 3); // fallback

  const scored = bySegment.map(p => {
    const t = p.tags || [];
    let score = 0;
    if (t.includes(tags.format))  score += 5;
    if (t.includes(tags.qty))     score += 3;
    if (t.includes(tags.budget))  score += 1;
    if (t.includes(tags.context)) score += 1;
    return { p, score };
  });

  scored.sort((a, b) => b.score - a.score);
  return scored.slice(0, 3).map(x => x.p);
}

// ── NAVIGATION ───────────────────────────────────────────────────────────────
let interTimer = null;

function goQ(n) {
  const prevId = idForIndex(S.cur);
  const nextId = idForIndex(n);
  trackNav(nextId);
  document.getElementById(prevId).classList.remove('active');
  S.cur = n;
  document.getElementById(nextId).classList.add('active');
  const pct = Math.round((n / (TOTAL_STEPS - 1)) * 100);
  document.getElementById('quiz-progress').style.width = pct + '%';
  if (n === 5) renderResults();
  document.getElementById('quiz-section').scrollIntoView({ behavior:'smooth', block:'start' });
}
function idForIndex(n) { return n === 'qi' ? 'qi' : 'q' + n; }
function scrollToQuiz() { document.getElementById('quiz-section').scrollIntoView({ behavior:'smooth' }); }

// ── INTERSTITIAL ─────────────────────────────────────────────────────────────
function launchInter() {
  trackNav('qi');
  document.getElementById(idForIndex(S.cur)).classList.remove('active');
  S.cur = 'qi';
  document.getElementById('qi').classList.add('active');
  document.getElementById('quiz-progress').style.width = '90%';
  document.getElementById('quiz-section').scrollIntoView({ behavior:'smooth', block:'start' });

  const fill = document.getElementById('qi-fill');
  fill.style.transition = 'none';
  fill.style.width = '0%';
  void fill.offsetWidth;
  fill.style.width = '100%';

  if (interTimer) clearTimeout(interTimer);
  interTimer = setTimeout(() => goQ(5), 1500);
}

function skipInter() {
  if (interTimer) { clearTimeout(interTimer); interTimer = null; }
  goQ(5);
}

// ── SELECT ───────────────────────────────────────────────────────────────────
function selectSingle(btn, group, nextQ) {
  document.querySelectorAll('[data-group="' + group + '"]').forEach(b => b.classList.remove('selected'));
  btn.classList.add('selected');
  S.answers[group] = btn.dataset.val;
  const nxtBtn = document.getElementById('nxt' + S.cur);
  if (nxtBtn) nxtBtn.disabled = false;
  setTimeout(() => nextQ === 5 ? launchInter() : goQ(nextQ), 280);
}

// ── RESULTS RENDER ───────────────────────────────────────────────────────────
function renderResults() {
  const segKey = calcSegment();
  const seg = SEGMENTS[segKey];

  document.getElementById('result-segment').textContent = seg.label;
  document.getElementById('result-sub').textContent = seg.sub;

  // products
  const products = pickProducts(segKey);
  const grid = document.getElementById('recos-grid');
  if (!products.length) {
    grid.innerHTML = '<div class="reco-empty">No products found for this segment. Make sure the "Spray Foam Insulation" collection is populated.</div>';
  } else {
    grid.innerHTML = products.map(p => `
      <a href="${p.url}" class="reco-card" data-locked-href="${p.url}">
        <div class="reco-img">
          ${p.image ? `<img src="${p.image}" alt="${p.title}" loading="lazy">` : '<span class="reco-img-fallback">No image</span>'}
        </div>
        <div class="reco-body">
          <div class="reco-title">${p.title}</div>
          <div class="reco-price">${p.price || ''}</div>
        </div>
      </a>
    `).join('');
  }

  // fire score event (only once)
  if (!T.sentScore) {
    T.sentScore = true;
    sendEvent('score', { segment: segKey, segmentLabel: seg.label, tags: deriveTags(segKey) });
  }

  // gate check — already unlocked from a previous visit?
  if (localStorage.getItem('fyf_unlocked') === '1') {
    unlockProducts(false);
  } else {
    grid.classList.add('locked');
    document.getElementById('email-gate').classList.remove('hidden');
  }
}

// ── EMAIL GATE ───────────────────────────────────────────────────────────────
function checkEmail() {
  const val = document.getElementById('email-input').value;
  const ok = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val) && document.getElementById('consent').checked;
  document.getElementById('btn-submit').disabled = !ok;
  document.getElementById('email-error').textContent = '';
}

function submitEmail() {
  const email = document.getElementById('email-input').value.trim();
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
    document.getElementById('email-error').textContent = 'Please enter a valid email address.';
    return;
  }
  T.sentEmail = true;
  const segKey = calcSegment();
  sendEvent('submit', { email, segment: segKey, segmentLabel: SEGMENTS[segKey].label, tags: deriveTags(segKey) });

  localStorage.setItem('fyf_unlocked', '1');
  localStorage.setItem('fyf_email', email);
  unlockProducts(true);
}

function unlockProducts(animate) {
  const grid = document.getElementById('recos-grid');
  const gate = document.getElementById('email-gate');
  grid.classList.remove('locked');
  gate.classList.add('hidden');
}

function toggleFaq(btn) { btn.closest('.faq-item').classList.toggle('open'); }

function restartQuiz() {
  S.cur = 0;
  S.answers = { problem:null, gun:null, quantity:null, budget:null };
  document.querySelectorAll('.q-opt').forEach(b => b.classList.remove('selected'));
  document.querySelectorAll('.btn-next-q').forEach(b => b.disabled = true);
  document.querySelectorAll('.quiz-screen').forEach(s => s.classList.remove('active'));
  document.getElementById('q0').classList.add('active');
  document.getElementById('quiz-progress').style.width = '0%';
  scrollToQuiz();
}
